import { t } from "@/i18n-js/instance";
import { isActionCompatibleWithTrigger } from "@/react/components/SettingsApp/Workflows/components/Actions/workflowsConfiguration";
import type { ActionOption } from "@/react/components/shared/WorkflowsV2/TriggerActionSelector/ActionSelector/utils/convertToTabGroupsData";
import { useSupportUnreleasedWorkflowsFeaturesEnabled } from "@circle-react/components/shared/Workflows/hooks/useSupportUnreleasedWorkflowsFeaturesEnabled";
import { availableEvents } from "../Events/availableEvents";
import { useWorkflow } from "../WorkflowForm/useWorkflow";
import { availableActions } from "./availableActions";

export const determineReleaseStatus = (
  released: boolean | (() => boolean) | undefined,
): boolean => {
  if (released === undefined) return true;
  return typeof released === "function" ? released() : released;
};

export interface ExtendedActionOption extends Partial<ActionOption<any>> {
  disabled?: boolean;
  disabledReason?: string;
}

// Create a map of trigger types to their labels for building disabled reasons
const triggerTypeToLabel = new Map(
  availableEvents.map(event => [event.value, event.label]),
);

export const useEligibleActions = () => {
  const shouldSupportUnreleasedWorkflowsFeaturesEnabled =
    useSupportUnreleasedWorkflowsFeaturesEnabled();

  const { type: workflowType, triggerType, isDynamic } = useWorkflow();

  // Filter out actions that are not released yet
  let eligibleActions = shouldSupportUnreleasedWorkflowsFeaturesEnabled
    ? availableActions
    : availableActions.filter(availableAction =>
        determineReleaseStatus(availableAction.released),
      );

  // Filter out actions that are not available for the current workflow type
  eligibleActions = eligibleActions.filter(
    action =>
      !action.availability || action.availability.includes(workflowType),
  );

  // Instead of filtering, mark incompatible actions as disabled with reasons
  const actionsWithDisabledState: ExtendedActionOption[] = eligibleActions
    .map(action => {
      // Check if action is compatible
      const isCompatible = isActionCompatibleWithTrigger(
        action.value,
        triggerType,
      );

      if (isCompatible) {
        return action;
      }

      // for bulk and scheduled we want to "omit" the action if its not compatible
      if (!isDynamic) {
        return undefined;
      }

      // Build disabled reason with trigger label
      const triggerLabel = triggerTypeToLabel.get(triggerType) || triggerType;
      const disabledReason = t(
        "workflows_v2.action_selector.action_restricted_by_trigger",
        {
          trigger: triggerLabel,
        },
      );

      return {
        ...action,
        disabled: true,
        disabledReason,
      };
    })
    .filter(Boolean) as ExtendedActionOption[];

  return { eligibleActions: actionsWithDisabledState };
};
